import { useEffect, useState } from "react";
import { motion } from "framer-motion";

/**
 * DevReel — animation motion-design ÉPURÉE (aucun personnage, aucune vidéo).
 * Met en scène un tableau de bord / une app ClickExpert qui "s'assemble" :
 *   - fenêtre applicative avec en-tête + monogramme ClickExpert
 *   - 3 KPIs qui s'incrémentent en douceur
 *   - une courbe de croissance qui se trace + barres animées
 *   - une barre de déploiement qui se remplit
 *   - cartes flottantes ("Déployé", "Nouveau projet")
 * 100% autonome : framer-motion (déjà présent) + SVG/Tailwind.
 * Palette du thème : ink #0B1220, gold #2F6FED, copper #14B8A6, cream #F4F6FB.
 */

// Petit compteur animé, sans dépendance externe.
function CountUp({ to, prefix = "", suffix = "", decimals = 0, duration = 1.8 }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf;
    let start;
    const step = (ts) => {
      if (start === undefined) start = ts;
      const p = Math.min((ts - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(to * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [to, duration]);
  return (
    <span>
      {prefix}
      {val.toLocaleString("fr-FR", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}
      {suffix}
    </span>
  );
}

const KPIS = [
  { label: "Revenu", to: 128, prefix: "", suffix: "k€", decimals: 0, color: "text-copper" },
  { label: "Utilisateurs", to: 24.8, suffix: "k", decimals: 1, color: "text-cream" },
  { label: "Croissance", to: 32, prefix: "+", suffix: "%", decimals: 0, color: "text-gold" },
];

// Barres du graphe (hauteurs relatives 0→1)
const BARS = [0.32, 0.48, 0.4, 0.62, 0.55, 0.78, 0.94];

export default function DevReel() {
  return (
    <div
      className="relative w-full h-full overflow-hidden select-none"
      data-testid="dev-reel"
      style={{ background: "linear-gradient(160deg, #0B1220 0%, #0d1a33 60%, #0B1220 100%)" }}
    >
      {/* Halo lumineux discret */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(90% 60% at 75% 15%, rgba(47,111,237,0.18), transparent 60%), radial-gradient(80% 60% at 15% 95%, rgba(20,184,166,0.14), transparent 60%)",
        }}
      />
      {/* Grille très subtile */}
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      {/* Fenêtre applicative principale */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: [0, -8, 0] }}
        transition={{
          opacity: { duration: 0.6 },
          y: { duration: 6, repeat: Infinity, ease: "easeInOut" },
        }}
        className="absolute left-[8%] right-[8%] top-[12%] rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-sm shadow-2xl"
        style={{ boxShadow: "0 30px 80px -20px rgba(0,0,0,0.6)" }}
      >
        {/* En-tête fenêtre */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
          <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
          <div className="ml-3 flex items-center gap-2">
            <svg viewBox="0 0 64 64" className="w-4 h-4">
              <rect x="2" y="2" width="60" height="60" rx="14" fill="#0B1220" stroke="#2F6FED" strokeWidth="3" />
              <path d="M25 20 L13 32 L25 44" fill="none" stroke="#2F6FED" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M39 20 L51 32 L39 44" fill="none" stroke="#2F6FED" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M35 16 L29 48" fill="none" stroke="#F4F6FB" strokeWidth="4" strokeLinecap="round" />
            </svg>
            <span className="text-[10px] uppercase tracking-[0.18em] text-cream/60">ClickExpert · Dashboard</span>
          </div>
        </div>

        {/* Corps */}
        <div className="p-4 space-y-4">
          {/* KPIs */}
          <div className="grid grid-cols-3 gap-2.5">
            {KPIS.map((k, i) => (
              <motion.div
                key={k.label}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + i * 0.15, duration: 0.5 }}
                className="rounded-lg bg-white/[0.05] border border-white/10 px-3 py-2.5"
              >
                <div className="text-[9px] uppercase tracking-wider text-cream/40">{k.label}</div>
                <div className={`mt-1 font-serif text-lg leading-none ${k.color}`}>
                  <CountUp to={k.to} prefix={k.prefix} suffix={k.suffix} decimals={k.decimals} />
                </div>
              </motion.div>
            ))}
          </div>

          {/* Graphe : barres + courbe */}
          <div className="relative rounded-lg bg-white/[0.03] border border-white/10 p-3 h-[128px]">
            <div className="text-[9px] uppercase tracking-wider text-cream/40 mb-1">Croissance mensuelle</div>
            <div className="relative h-[92px]">
              {/* Barres */}
              <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-1.5 h-full px-1">
                {BARS.map((h, i) => (
                  <motion.div
                    key={i}
                    initial={{ scaleY: 0 }}
                    animate={{ scaleY: h }}
                    transition={{ delay: 0.6 + i * 0.08, duration: 0.7, ease: "easeOut" }}
                    style={{ transformOrigin: "bottom", height: "100%" }}
                    className="flex-1 rounded-t-sm bg-gradient-to-t from-gold/25 to-gold/60"
                  />
                ))}
              </div>
              {/* Courbe (aire + ligne) */}
              <svg viewBox="0 0 280 92" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
                <defs>
                  <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#14B8A6" stopOpacity="0.35" />
                    <stop offset="1" stopColor="#14B8A6" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <motion.path
                  d="M0 74 L46 62 L93 66 L140 44 L186 50 L233 24 L280 8 L280 92 L0 92 Z"
                  fill="url(#areaFill)"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 1] }}
                  transition={{ delay: 1.2, duration: 0.8 }}
                />
                <motion.path
                  d="M0 74 L46 62 L93 66 L140 44 L186 50 L233 24 L280 8"
                  fill="none"
                  stroke="#14B8A6"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: [0, 1, 1] }}
                  transition={{ duration: 6, times: [0, 0.35, 1], repeat: Infinity, ease: "easeInOut" }}
                />
              </svg>
            </div>
          </div>

          {/* Barre de déploiement */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[9px] uppercase tracking-wider text-cream/40">Déploiement</span>
              <span className="text-[9px] text-copper">en direct</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-gold to-copper"
                initial={{ width: "0%" }}
                animate={{ width: ["0%", "100%"] }}
                transition={{ duration: 3, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
              />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Carte flottante : Déployé */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: [0, -6, 0] }}
        transition={{
          opacity: { delay: 1.6, duration: 0.5 },
          scale: { delay: 1.6, duration: 0.5 },
          y: { duration: 5, repeat: Infinity, ease: "easeInOut" },
        }}
        className="absolute right-[5%] top-[6%] flex items-center gap-2 rounded-lg bg-ink/80 border border-copper/40 px-3 py-2 backdrop-blur"
      >
        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-copper/20">
          <svg viewBox="0 0 24 24" className="w-3 h-3" fill="none" stroke="#14B8A6" strokeWidth="3">
            <path d="M20 6 L9 17 L4 12" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        <span className="text-[10px] text-cream/80">App déployée</span>
      </motion.div>

      {/* Carte flottante : build */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: [0, 7, 0] }}
        transition={{
          opacity: { delay: 2, duration: 0.5 },
          y: { duration: 6.5, repeat: Infinity, ease: "easeInOut" },
        }}
        className="absolute left-[4%] bottom-[16%] flex items-center gap-2 rounded-lg bg-ink/80 border border-gold/40 px-3 py-2 backdrop-blur"
      >
        <span className="w-2 h-2 rounded-full bg-gold animate-pulse" />
        <span className="text-[10px] text-cream/80">Build · 100% réussi</span>
      </motion.div>

      {/* Légende */}
      <div className="absolute bottom-5 left-5 right-5">
        <p className="font-serif text-cream text-lg leading-tight">
          Votre app <span className="text-copper">ClickExpert</span>, prête à scaler.
        </p>
        <p className="text-cream/50 text-[12px] mt-1 tracking-wide">Conception · développement · déploiement</p>
      </div>
    </div>
  );
}
